import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowRight, Camera, Brain, Sparkle, ScanSmiley, SealCheck, ShieldCheck,
  DeviceMobile, CurrencyCircleDollar, EnvelopeSimple, ChatCircleDots, FilePdf,
  CheckCircle, Receipt, Storefront, Trophy, Flame, Airplane, ArrowsClockwise,
  ChartLineUp, User, Briefcase, Confetti, Tag,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import ContactForm from "@/components/ContactForm";

const ease = [0.22, 1, 0.36, 1];
const IMG_GROCERY = "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxncm9jZXJ5JTIwc2hvcHBpbmd8ZW58MHx8fHwxNzg4Mzc3NjkwfDA&ixlib=rb-4.1.0&q=85&w=1200";
const IMG_TRAVEL = "https://images.unsplash.com/photo-1504150558240-0b4fd8946624?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHRyYXZlbHxlbnwwfHx8fDE3ODgzNzc2OTB8MA&ixlib=rb-4.1.0&q=85&w=1200";

export default function Landing() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("personal");

  const demo = async () => {
    setBusy(true);
    try {
      const u = await login("demo@sliply.app", "Demo@2026");
      navigate(u.onboarded ? "/" : "/onboarding");
    } catch {
      toast.error("Demo login failed");
    } finally { setBusy(false); }
  };

  const fadeUp = {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease, delay: i * 0.07 } }),
  };
  const rise = reduce ? {} : { initial: { opacity: 0, y: 26 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.55, ease } };

  return (
    <div className="min-h-screen bg-riq-bg text-slate-900 overflow-x-hidden" data-testid="landing-page">
      <Nav demo={demo} busy={busy} />

      {/* HERO */}
      <section className="relative pt-32 pb-16 sm:pb-24 px-6" data-testid="hero">
        <Blob className="-top-28 -left-32 bg-riq-primary/20" />
        <Blob className="top-32 -right-24 bg-riq-gold/20" />
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={0}
            className="inline-flex items-center gap-2 rounded-full border border-riq-primary/20 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold text-riq-primary">
            <Sparkle size={14} weight="fill" /> AI receipt intelligence · built for South Africa
          </motion.div>
          <motion.h1 initial="hidden" animate="show" variants={fadeUp} custom={1}
            className="font-head font-black tracking-tighter mt-6 text-4xl sm:text-6xl leading-[1.02]">
            Stop losing slips.<br /><span className="text-riq-primary">Start saving smarter.</span>
          </motion.h1>
          <motion.p initial="hidden" animate="show" variants={fadeUp} custom={2}
            className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl mx-auto">
            Snap any till slip and Sliply turns it into a searchable spending brain — compare grocery prices,
            track warranties &amp; subscriptions, budget your trips, and export tidy reports. For homes and businesses.
          </motion.p>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={3}
            className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="hero-cta-get-started"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-riq-primary text-white px-7 py-3.5 font-semibold shadow-lg shadow-riq-primary/25 hover:-translate-y-0.5 active:scale-95 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-riq-primary focus-visible:ring-offset-2">
              Sign up free <ArrowRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
            </Link>
            <button onClick={demo} disabled={busy} data-testid="hero-cta-demo"
              className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-riq-gold/60 bg-white text-slate-800 px-7 py-3.5 font-semibold hover:-translate-y-0.5 active:scale-95 transition-transform disabled:opacity-60">
              <ScanSmiley size={18} weight="duotone" className="text-riq-gold" /> Explore the demo
            </button>
          </motion.div>
          <motion.div initial="hidden" animate="show" variants={fadeUp} custom={4}
            className="mt-7 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-slate-500">
            {["No card needed", "Free to try", "ZAR ready", "Works offline (PWA)"].map((t) => (
              <span key={t} className="inline-flex items-center gap-1.5"><CheckCircle size={15} weight="fill" className="text-riq-primary" /> {t}</span>
            ))}
          </motion.div>
        </div>

        {/* Hero visual — floating receipt + live price chip */}
        <motion.div initial={reduce ? {} : { opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease, delay: 0.35 }}
          className="max-w-4xl mx-auto mt-14 relative">
          <div className="rounded-[28px] border border-riq-border bg-white shadow-[0_20px_60px_rgba(110,63,252,0.12)] p-3">
            <div className="rounded-3xl overflow-hidden relative aspect-[16/9]">
              <img src={IMG_GROCERY} alt="Comparing grocery prices" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
              <div className="absolute left-5 bottom-5 right-5 flex items-end justify-between gap-3">
                <div className="text-white">
                  <div className="text-[11px] font-semibold tracking-[0.18em] uppercase opacity-80">Where's it cheapest?</div>
                  <div className="font-head text-2xl font-bold">Full Cream Milk 2L</div>
                </div>
                <div className="rounded-2xl bg-white/95 backdrop-blur px-4 py-3 shadow-lg">
                  <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">Cheapest</div>
                  <div className="font-head text-xl font-black text-riq-primary">R32,99</div>
                  <div className="text-[10px] text-[#1f7a48] font-semibold">save R12 vs avg</div>
                </div>
              </div>
            </div>
          </div>
          <FloatChip className="-top-4 -left-3 sm:-left-6" icon={Camera} label="Snap → done in 3s" />
          <FloatChip className="-bottom-4 -right-3 sm:-right-6" icon={Trophy} label="+5 Moolas earned" gold />
        </motion.div>
      </section>

      {/* MODE TOGGLE + BENTO */}
      <section className="px-6 py-8 sm:py-14" data-testid="features">
        <motion.div {...rise} className="text-center max-w-2xl mx-auto">
          <div className="text-xs tracking-[0.2em] uppercase text-slate-500 font-semibold">Everything in one place</div>
          <h2 className="font-head text-3xl sm:text-4xl tracking-tight mt-3">One scan. A dozen superpowers.</h2>
          <p className="text-slate-600 mt-3">Switch to see what you get, whether it's the family shop or the company books.</p>
          <div className="mt-7 inline-flex rounded-full bg-white border border-riq-border p-1 shadow-sm" data-testid="mode-toggle">
            {[["personal", "Personal", User], ["business", "Business", Briefcase]].map(([k, l, Icon]) => (
              <button key={k} data-testid={`mode-${k}`} onClick={() => setMode(k)}
                className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${mode === k ? "bg-riq-primary text-white" : "text-slate-500 hover:text-slate-800"}`}>
                <Icon size={16} weight="duotone" /> {l}
              </button>
            ))}
          </div>
        </motion.div>

        <div className="max-w-6xl mx-auto mt-10 grid grid-cols-1 md:grid-cols-12 gap-5" key={mode}>
          {(mode === "personal" ? PERSONAL : BUSINESS).map((f, i) => (
            <BentoCard key={f.title} f={f} i={i} reduce={reduce} />
          ))}
        </div>
      </section>

      {/* GAMIFICATION */}
      <section className="relative py-20 px-6 mt-6" style={{ background: "#FFF9F0" }} data-testid="gamification-section">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-center">
          <motion.div {...rise}>
            <div className="inline-flex items-center gap-2 rounded-full bg-riq-gold/15 text-[#B25E00] px-3.5 py-1.5 text-xs font-bold uppercase tracking-wide">
              <Confetti size={14} weight="fill" /> Saving, but make it fun
            </div>
            <h2 className="font-head text-3xl sm:text-4xl tracking-tight mt-4">Earn Moolas. Build streaks. Unlock badges.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">
              Every slip you scan earns Moolas and keeps your streak alive. Hit milestones to unlock achievement
              badges and bonus rewards — with a little celebration each time. Good money habits, minus the boredom.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              {[[Flame, "Scan streaks"], [Trophy, "Achievement badges"], [Sparkle, "Bonus Moolas"], [ChartLineUp, "Saver levels"]].map(([Icon, l]) => (
                <span key={l} className="inline-flex items-center gap-2 rounded-full bg-white border border-riq-gold/25 px-4 py-2 text-sm font-medium text-slate-700">
                  <Icon size={16} weight="duotone" className="text-riq-gold" /> {l}
                </span>
              ))}
            </div>
          </motion.div>
          <motion.div {...rise} className="grid grid-cols-2 gap-4">
            <StatCard big="52" label="Moolas earned" icon={Sparkle} />
            <StatCard big="14🔥" label="Day streak" icon={Flame} />
            <StatCard big="9/12" label="Badges unlocked" icon={Trophy} />
            <StatCard big="Pro" label="Saver level" icon={ChartLineUp} />
          </motion.div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 px-6" data-testid="how-it-works">
        <motion.h2 {...rise} className="font-head text-3xl sm:text-4xl tracking-tight text-center">Three taps to smarter spending</motion.h2>
        <div className="max-w-5xl mx-auto mt-12 grid md:grid-cols-3 gap-6">
          {[
            { n: "01", icon: Camera, t: "Snap or forward", d: "Photograph a slip, upload a PDF, or forward the store's email. Done." },
            { n: "02", icon: Brain, t: "AI does the boring bit", d: "We read the merchant, items, totals and dates — and file it all automatically." },
            { n: "03", icon: Storefront, t: "Save & compare", d: "See where it's cheapest, track warranties, budget trips and export reports." },
          ].map((s, i) => (
            <motion.div key={s.n} {...rise} transition={{ ...rise.transition, delay: i * 0.1 }}
              className="rounded-3xl border border-riq-border bg-white p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
              <div className="flex items-center justify-between">
                <span className="grid h-12 w-12 place-items-center rounded-2xl bg-riq-sage text-riq-primary"><s.icon size={24} weight="duotone" /></span>
                <span className="font-head text-3xl font-black text-riq-border">{s.n}</span>
              </div>
              <h3 className="font-head text-xl font-bold mt-5">{s.t}</h3>
              <p className="text-slate-600 mt-2 text-sm leading-relaxed">{s.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="bg-slate-900 text-white pt-20 pb-10 px-6" data-testid="footer">
        <div className="max-w-5xl mx-auto text-center">
          <motion.h2 {...rise} className="font-head text-4xl sm:text-5xl font-black tracking-tighter">Join Sliply today.</motion.h2>
          <motion.p {...rise} className="text-slate-300 mt-4 max-w-lg mx-auto">Free to start, no card needed. Add it to your home screen and never lose a slip again.</motion.p>
          <motion.div {...rise} className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/register" data-testid="footer-cta-get-started"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-riq-primary text-white px-7 py-3.5 font-semibold hover:-translate-y-0.5 active:scale-95 transition-transform">
              Sign up free <ArrowRight size={18} weight="bold" />
            </Link>
            <button onClick={demo} disabled={busy} data-testid="footer-cta-demo"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 bg-white/5 text-white px-7 py-3.5 font-semibold hover:bg-white/10 transition-colors disabled:opacity-60">
              <ScanSmiley size={18} weight="duotone" /> Try the demo
            </button>
          </motion.div>
          <div className="mt-6 flex flex-wrap justify-center gap-x-5 gap-y-2 text-xs text-slate-400">
            <span className="inline-flex items-center gap-1.5"><DeviceMobile size={14} weight="duotone" /> Install as an app</span>
            <span className="inline-flex items-center gap-1.5"><CurrencyCircleDollar size={14} weight="duotone" /> ZAR &amp; multi-currency</span>
            <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} weight="duotone" /> Your data, anonymised for prices</span>
          </div>
        </div>

        <div className="max-w-xl mx-auto mt-14">
          <h3 className="font-head text-xl font-bold text-center">Questions? Say hello.</h3>
          <div className="mt-5"><ContactForm compact /></div>
        </div>

        <div className="max-w-6xl mx-auto mt-14 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2.5">
            <img src="/sliply-mark.png" alt="Sliply" className="w-8 h-8" />
            <div>
              <div className="font-head font-bold leading-none">Sliply</div>
              <div className="text-[11px] text-slate-400 tracking-[0.15em] uppercase">sliply.co.za</div>
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm text-slate-400">
            <Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link>
            <Link to="/hpi" data-testid="footer-hpi" className="hover:text-white transition-colors">Price Index</Link>
            <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            <Link to="/refunds" className="hover:text-white transition-colors">Refunds</Link>
            <Link to="/login" className="hover:text-white transition-colors">Log in</Link>
            <span>© {new Date().getFullYear()} Sliply</span>
          </div>
        </div>
      </section>
    </div>
  );
}

/* ---------------- feature data ---------------- */
const PERSONAL = [
  { span: "md:col-span-8", tint: "primary", icon: Camera, title: "Snap it, we file it", body: "AI reads every till slip, PDF or forwarded email into a searchable spending history — merchant, items, totals and dates, automatically.", image: null, tags: ["OCR", "Auto-sort", "Email forwarding"] },
  { span: "md:col-span-4", tint: "plain", icon: ArrowsClockwise, title: "Subscriptions tracker", body: "Spot recurring bills and see them land on their billing dates so nothing surprises you." },
  { span: "md:col-span-4", tint: "plain", icon: SealCheck, title: "Warranties & Claim Packs", body: "Track guarantees per item and generate a ready-to-send claim pack with proof in one tap." },
  { span: "md:col-span-8", tint: "image", icon: Storefront, title: "Where's it cheapest?", body: "Compare live Takealot, Pick n Pay & Woolworths prices alongside anonymous community slips — cheapest wins.", image: IMG_GROCERY },
  { span: "md:col-span-4", tint: "plain", icon: Airplane, title: "Trips & holiday budgets", body: "Group a getaway's spend, set a cap and watch the progress bar." },
  { span: "md:col-span-4", tint: "plain", icon: ChatCircleDots, title: "Ask AI your money", body: "\"How much on groceries in April?\" — just ask and get an instant answer." },
  { span: "md:col-span-4", tint: "gold", icon: Trophy, title: "Rewards & streaks", body: "Earn Moolas, keep streaks alive and unlock badges as you go." },
];
const BUSINESS = [
  { span: "md:col-span-8", tint: "image", icon: Airplane, title: "Business trips & travel", body: "Group expenses by trip or client, add descriptions, set budgets and keep every slip audit-ready.", image: IMG_TRAVEL },
  { span: "md:col-span-4", tint: "primary", icon: FilePdf, title: "PDF expense reports", body: "Monthly & per-trip reports with charts and category splits — emailed to you in a tap." },
  { span: "md:col-span-4", tint: "plain", icon: Camera, title: "Capture any way", body: "Snap, upload or forward supplier emails — the AI files each one instantly." },
  { span: "md:col-span-4", tint: "plain", icon: ChartLineUp, title: "Analytics & insights", body: "Category breakdowns and stacked monthly trends across the business." },
  { span: "md:col-span-4", tint: "plain", icon: ChatCircleDots, title: "Ask AI about spend", body: "Query your company spending in plain English, no spreadsheets." },
  { span: "md:col-span-8", tint: "plain", icon: CurrencyCircleDollar, title: "Multi-currency, ZAR-first", body: "Auto-detects your country and currency, so cross-border receipts just work — perfect for imports and travel.", tags: ["ZAR", "Auto-detect", "FX ready"] },
  { span: "md:col-span-4", tint: "plain", icon: SealCheck, title: "Warranty proof", body: "Keep asset guarantees and generate claim packs when equipment fails." },
];

/* ---------------- pieces ---------------- */
function Nav({ demo, busy }) {
  return (
    <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/50 shadow-sm" data-testid="landing-nav">
      <div className="max-w-6xl mx-auto px-5 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
          <img src="/sliply-mark.png" alt="Sliply" className="w-9 h-9 p-1.5 rounded-xl bg-riq-primary" />
          <div className="leading-none">
            <div className="font-head font-black text-lg tracking-tight">Sliply</div>
            <div className="text-[10px] text-riq-primary/70 tracking-[0.18em] uppercase">sliply.co.za</div>
          </div>
        </Link>
        <div className="flex items-center gap-2 sm:gap-3">
          <Link to="/pricing" data-testid="nav-pricing" className="hidden sm:inline-flex text-sm font-semibold text-slate-600 hover:text-riq-primary px-3 py-2 transition-colors">Pricing</Link>
          <button onClick={demo} disabled={busy} data-testid="nav-demo" className="hidden sm:inline-flex text-sm font-semibold text-slate-600 hover:text-riq-primary px-3 py-2 transition-colors disabled:opacity-60">Explore demo</button>
          <Link to="/login" data-testid="nav-login" className="text-sm font-semibold text-slate-600 hover:text-riq-primary px-3 py-2 transition-colors">Log in</Link>
          <Link to="/register" data-testid="nav-signup" className="inline-flex items-center gap-1.5 rounded-full bg-riq-primary text-white px-4 py-2 text-sm font-semibold hover:-translate-y-0.5 active:scale-95 transition-transform">
            Sign up free
          </Link>
        </div>
      </div>
    </header>
  );
}

function Blob({ className = "" }) {
  return <div className={`pointer-events-none absolute h-72 w-72 rounded-full blur-3xl opacity-70 ${className}`} aria-hidden />;
}

function FloatChip({ className = "", icon: Icon, label, gold }) {
  return (
    <div className={`absolute ${className} hidden sm:flex items-center gap-2 rounded-2xl bg-white shadow-lg border border-riq-border px-4 py-2.5`}>
      <span className={`grid h-8 w-8 place-items-center rounded-xl ${gold ? "bg-riq-gold/15 text-riq-gold" : "bg-riq-sage text-riq-primary"}`}><Icon size={18} weight="duotone" /></span>
      <span className="text-sm font-semibold text-slate-800 whitespace-nowrap">{label}</span>
    </div>
  );
}

function BentoCard({ f, i, reduce }) {
  const tintMap = {
    primary: "bg-riq-primary text-white border-riq-primary",
    gold: "bg-[#FFF9F0] border-riq-gold/25",
    plain: "bg-white border-riq-border",
    image: "border-riq-border text-white",
  };
  const isPrimary = f.tint === "primary";
  const isImage = f.tint === "image";
  return (
    <motion.div
      initial={reduce ? {} : { opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.5, ease, delay: (i % 4) * 0.08 }}
      data-testid={`feature-${f.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
      className={`relative overflow-hidden rounded-3xl border p-8 shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:shadow-lg hover:-translate-y-1 transition-all duration-300 ${f.span} ${tintMap[f.tint]}`}
    >
      {isImage && (
        <>
          <img src={f.image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/85 via-slate-900/55 to-slate-900/25" />
        </>
      )}
      <div className="relative">
        <span className={`grid h-12 w-12 place-items-center rounded-2xl ${isPrimary ? "bg-white/15 text-white" : isImage ? "bg-white/20 text-white backdrop-blur" : f.tint === "gold" ? "bg-riq-gold/15 text-riq-gold" : "bg-riq-sage text-riq-primary"}`}>
          <f.icon size={24} weight="duotone" />
        </span>
        <h3 className={`font-head text-xl font-bold mt-5 ${isPrimary || isImage ? "text-white" : "text-slate-900"}`}>{f.title}</h3>
        <p className={`mt-2 text-sm leading-relaxed ${isPrimary ? "text-white/85" : isImage ? "text-white/90" : "text-slate-600"}`}>{f.body}</p>
        {f.tags && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {f.tags.map((t) => (
              <span key={t} className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold ${isPrimary ? "bg-white/15 text-white" : "bg-riq-sage text-riq-primary"}`}><Tag size={10} weight="fill" /> {t}</span>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
}

function StatCard({ big, label, icon: Icon }) {
  return (
    <div className="rounded-3xl bg-white border border-riq-gold/25 p-6 shadow-[0_8px_32px_rgba(245,166,35,0.08)]">
      <Icon size={22} weight="duotone" className="text-riq-gold" />
      <div className="font-head text-3xl font-black mt-3">{big}</div>
      <div className="text-sm text-slate-500 mt-1">{label}</div>
    </div>
  );
}
